import React, { useEffect, useRef } from "react";
import Navbar from "../components/Navbar";
import Topbar from "../components/Topbar";
import Footer from "../components/Footer";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Link } from "react-router-dom";
import hero from "../assets/hero.png";
import featured1 from "../assets/featured-1.png";
import featured2 from "../assets/featured-2.png";
import featured3 from "../assets/featured-3.png";

gsap.registerPlugin(ScrollTrigger);

const Blog = () => {
  const heroRef = useRef(null);
  const contentRef = useRef(null);

  const blogPosts = [
    {
      id: 1,
      title: "The Art of Layering Fragrances: A Complete Guide",
      excerpt: "Discover the secrets of fragrance layering to create your unique signature scent. Learn professional techniques used by perfumers.",
      image: featured1,
      date: "January 15, 2025",
      category: "Tips & Tricks",
      readTime: "5 min read"
    },
    {
      id: 2,
      title: "Spring 2025 Fragrance Trends: What's Hot This Season",
      excerpt: "Explore the latest fragrance trends for spring 2025, from fresh florals to innovative woody blends that are taking the perfume world by storm.",
      image: featured2,
      date: "January 12, 2025",
      category: "Trends",
      readTime: "7 min read"
    },
    {
      id: 3,
      title: "How to Make Your Fragrance Last All Day",
      excerpt: "Professional tips and tricks to maximize your fragrance's longevity. Learn about pulse points, application techniques, and storage methods.",
      image: featured3,
      date: "January 10, 2025",
      category: "Education",
      readTime: "6 min read"
    },
    {
      id: 4,
      title: "The Psychology of Scent: How Fragrances Affect Mood",
      excerpt: "Dive into the fascinating world of aromatherapy and discover how different fragrance notes can influence your emotions and well-being.",
      image: hero,
      date: "January 8, 2025",
      category: "Science",
      readTime: "8 min read"
    },
    {
      id: 5,
      title: "Building Your Fragrance Wardrobe: Essential Scents for Every Occasion",
      excerpt: "Create a well-rounded fragrance collection with our guide to essential scents for work, casual wear, special occasions, and seasonal changes.",
      image: featured1,
      date: "January 5, 2025",
      category: "Collection",
      readTime: "10 min read"
    },
    {
      id: 6,
      title: "Fragrance Notes Explained: Understanding Top, Middle, and Base Notes",
      excerpt: "Learn about the structure of fragrances and how different notes work together to create complex, beautiful scents that evolve over time.",
      image: featured2,
      date: "January 3, 2025",
      category: "Education",
      readTime: "6 min read"
    }
  ];

  useEffect(() => {
    gsap.fromTo(
      heroRef.current,
      { opacity: 0, y: 50 },
      { opacity: 1, y: 0, duration: 1, ease: "power3.out" }
    );

    gsap.fromTo(
      contentRef.current.querySelectorAll(".blog-card"),
      { opacity: 0, y: 30 },
      {
        opacity: 1,
        y: 0,
        duration: 0.8,
        stagger: 0.2,
        ease: "power3.out",
        scrollTrigger: {
          trigger: contentRef.current,
          start: "top 80%",
          toggleActions: "play none none none",
        },
      }
    );
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Topbar />
      <div className="bg-black">
        <Navbar />
      </div>
      
      {/* Hero Section */}
      <div ref={heroRef} className="relative py-20 px-4 text-center text-white bg-gray-900">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-5xl md:text-6xl font-bold mb-6 text-[#A88006]">
            Fragrance Blog
          </h1>
          <p className="text-xl text-gray-300 leading-relaxed">
            Discover the world of fragrances through expert insights, tips, and the latest trends in perfumery.
          </p>
        </div>
      </div>

      {/* Featured Post */}
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="bg-white rounded-2xl border border-[#A88006]/20 shadow-lg overflow-hidden mb-16">
          <div className="grid md:grid-cols-2 gap-0">
            <div className="relative h-64 md:h-auto">
              <img 
                src={blogPosts[0].image} 
                alt={blogPosts[0].title}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-black/30 to-transparent"></div>
            </div>
            <div className="p-8 flex flex-col justify-center">
              <div className="flex items-center gap-4 mb-4">
                <span className="bg-[#A88006] text-white px-3 py-1 rounded-full text-sm font-semibold">
                  Featured
                </span>
                <span className="text-[#A88006] text-sm">{blogPosts[0].category}</span>
              </div>
              <h2 className="text-3xl font-bold text-black mb-4">{blogPosts[0].title}</h2>
              <p className="text-gray-700 mb-6 leading-relaxed">{blogPosts[0].excerpt}</p>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4 text-sm text-gray-600">
                  <span>{blogPosts[0].date}</span>
                  <span>•</span>
                  <span>{blogPosts[0].readTime}</span>
                </div>
                <Link 
                  to={`/blog/${blogPosts[0].id}`}
                  className="bg-gradient-to-r from-[#A88006] to-[#9F7800] text-white font-semibold px-6 py-2 rounded-lg hover:opacity-90 transition-all"
                >
                  Read More
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Blog Grid */}
      <div ref={contentRef} className="max-w-6xl mx-auto px-4 pb-16">
        <h2 className="text-3xl font-bold text-black mb-12 text-center">Latest Articles</h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {blogPosts.slice(1).map((post) => (
            <div key={post.id} className="blog-card bg-white rounded-xl border border-[#A88006]/20 shadow-lg overflow-hidden hover:border-[#A88006]/40 transition-all group">
              <div className="relative h-48 overflow-hidden">
                <img 
                  src={post.image} 
                  alt={post.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
                <div className="absolute top-4 left-4">
                  <span className="bg-[#A88006]/90 text-white px-3 py-1 rounded-full text-sm font-semibold">
                    {post.category}
                  </span>
                </div>
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold text-black mb-3 group-hover:text-[#A88006] transition-colors">
                  {post.title}
                </h3>
                <p className="text-gray-700 text-sm mb-4 leading-relaxed">{post.excerpt}</p>
                <div className="flex items-center justify-between">
                  <div className="text-xs text-gray-600">
                    <div>{post.date}</div>
                    <div>{post.readTime}</div>
                  </div>
                  <Link 
                    to={`/blog/${post.id}`}
                    className="text-[#A88006] hover:text-[#9F7800] font-semibold text-sm transition-colors"
                  >
                    Read More →
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Newsletter Signup */}
        <div className="mt-16 bg-gradient-to-r from-[#A88006]/10 to-[#9F7800]/10 p-8 rounded-xl border border-[#A88006]/20 text-center">
          <h3 className="text-2xl font-bold text-black mb-4">Stay Updated</h3>
          <p className="text-gray-700 mb-6">
            Subscribe to our newsletter for the latest fragrance tips, trends, and exclusive offers.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
            <input
              type="email"
              placeholder="Enter your email"
              className="flex-1 px-4 py-3 rounded-lg bg-white border border-gray-300 text-black placeholder-gray-500 focus:outline-none focus:border-[#A88006]"
            />
            <button className="bg-gradient-to-r from-[#A88006] to-[#9F7800] text-white font-semibold px-6 py-3 rounded-lg hover:opacity-90 transition-all">
              Subscribe
            </button>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Blog;